/**
 * 题库数据包加载与完整性校验。
 *
 * 磁盘上的 JSON（packages/core-schema/src/data/*.json）采用「证据库 + 引用 id」的
 * 紧凑写法，避免同一份权威数据在卡片中重复 drift。加载时解析为 SPEC §3.3 规定的
 * 完整 `ConstructiveStrategyCard.mandatoryEvidences` 形态。
 */
import { STAGE_SCHEDULE, getPairDealPlans, validateSchedule } from './schedule.ts';
import { SANDBOX_LABEL } from './types.ts';
import type {
  CoachRubric,
  ConstructiveStrategyCard,
  CrossExTaskCard,
  DebateDag,
  MandatoryEvidence,
  MotionPackage,
  SandboxCard,
  SideBrief,
  Side,
} from './types.ts';

/* ------------------------------------------------------------------ */
/* 磁盘 JSON 的源类型                                                  */
/* ------------------------------------------------------------------ */

export type ConstructiveCardSource = Omit<ConstructiveStrategyCard, 'mandatoryEvidences'> & {
  /** 引用 evidenceLibrary 中的证据 id */
  mandatoryEvidenceIds: string[];
};

export interface MotionPackageSource {
  motionId: string;
  motion: string;
  title: string;
  version: string;
  sides: Record<Side, SideBrief>;
  constructiveCards: ConstructiveCardSource[];
  crossExCards: CrossExTaskCard[];
  sandboxCards: SandboxCard[];
  evidenceLibrary: MandatoryEvidence[];
  dag: DebateDag;
  coachRubric: CoachRubric;
  coachingNotes?: string[];
}

export class MotionDataError extends Error {
  readonly problems: string[];
  constructor(problems: string[]) {
    super(`题库数据校验失败（${problems.length} 项）：\n- ${problems.join('\n- ')}`);
    this.name = 'MotionDataError';
    this.problems = problems;
  }
}

/* ------------------------------------------------------------------ */
/* 校验                                                                */
/* ------------------------------------------------------------------ */

export interface MotionAuditResult {
  problems: string[];
  warnings: string[];
}

const CONSTRUCTIVE_STAGES = STAGE_SCHEDULE.filter((s) => s.cardSlot === 'CONSTRUCTIVE');
const CROSS_EXAM_STAGES = STAGE_SCHEDULE.filter((s) => s.cardSlot === 'CROSS_EXAM');
/** 沙盘主题数量（三大决战沙盘池：2 轮对辩各抽 1 张） */
const SANDBOX_TOPIC_COUNT = Object.keys(SANDBOX_LABEL).length;
const AFF_ARCHETYPES = new Set(['A1_ELITE_SPLIT', 'A2_COGNITIVE_OFFLOAD', 'A3_ECONOMIC_DEVALUATION']);
const NEG_ARCHETYPES = new Set(['N1_ONTOLOGY_CARRIER', 'N2_HALLUCINATION_RISK', 'N3_COGNITIVE_LEVER']);
const EVIDENCE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function compileRegex(pattern: string): boolean {
  try {
    new RegExp(pattern, 'u');
    return true;
  } catch {
    try {
      new RegExp(pattern);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * 深度审计题库数据。返回 problems（必须修复）与 warnings（建议修复）。
 * 内容规模要求（30 份权威论据、6+6+3 卡片等）以 warning 形式报告，
 * 由内容验收测试断言其为零，保证应用可先用占位数据启动。
 */
export function auditMotionPackage(source: MotionPackageSource): MotionAuditResult {
  const problems: string[] = [];
  const warnings: string[] = [];
  const evidenceIds = new Set<string>();

  if (!isNonEmptyString(source.motionId)) problems.push('motionId 不能为空');
  if (!isNonEmptyString(source.motion)) problems.push('motion 不能为空');
  if (!isNonEmptyString(source.title)) problems.push('title 不能为空');
  if (!source.sides?.AFF || !source.sides?.NEG) problems.push('sides 必须同时包含 AFF 与 NEG');

  problems.push(...validateSchedule());

  /* 证据库 */
  if (!Array.isArray(source.evidenceLibrary)) {
    problems.push('evidenceLibrary 必须是数组');
  } else {
    for (const evidence of source.evidenceLibrary) {
      if (!isNonEmptyString(evidence.id) || !EVIDENCE_ID_PATTERN.test(evidence.id)) {
        problems.push(`证据 id 非法：${String(evidence.id)}（需为 kebab-case）`);
        continue;
      }
      if (evidenceIds.has(evidence.id)) problems.push(`证据 id 重复：${evidence.id}`);
      evidenceIds.add(evidence.id);
      if (!isNonEmptyString(evidence.shortTitle)) problems.push(`证据 ${evidence.id} 缺 shortTitle`);
      if (!isNonEmptyString(evidence.exactQuoteOrStats)) {
        problems.push(`证据 ${evidence.id} 缺 exactQuoteOrStats`);
      }
      if (!Array.isArray(evidence.mustIncludeKeywords) || evidence.mustIncludeKeywords.length === 0) {
        problems.push(`证据 ${evidence.id} 至少需要一个 mustIncludeKeywords`);
      }
      for (const keyword of evidence.mustIncludeKeywords ?? []) {
        if (!isNonEmptyString(keyword)) problems.push(`证据 ${evidence.id} 存在空关键词`);
      }
      for (const misquote of evidence.forbiddenMisquotes ?? []) {
        if (!isNonEmptyString(misquote.pattern)) {
          problems.push(`证据 ${evidence.id} 存在空 forbiddenMisquotes.pattern`);
        } else if (!compileRegex(misquote.pattern)) {
          problems.push(`证据 ${evidence.id} 的 forbiddenMisquotes 正则无法编译：${misquote.pattern}`);
        }
        if (!isNonEmptyString(misquote.correctionAlert)) {
          problems.push(`证据 ${evidence.id} 存在空 correctionAlert`);
        }
      }
    }
  }

  const knownEvidence = (id: string): boolean => evidenceIds.has(id);
  const checkEvidenceRefs = (ids: string[] | undefined, label: string): void => {
    for (const id of ids ?? []) {
      if (!knownEvidence(id)) problems.push(`${label} 引用了不存在的证据：${id}`);
    }
  };

  /* 申论策略卡 */
  const seenConstructiveStages = new Set<number>();
  if (!Array.isArray(source.constructiveCards)) {
    problems.push('constructiveCards 必须是数组');
  } else {
    for (const card of source.constructiveCards) {
      const label = `申论卡 ${card.cardId || '(无 id)'}`;
      if (!isNonEmptyString(card.cardId)) problems.push('申论卡缺 cardId');
      const stage = CONSTRUCTIVE_STAGES.find((s) => s.index === card.stageIndex);
      if (!stage) {
        problems.push(`${label} 的 stageIndex=${card.stageIndex} 不是申论环节`);
      } else {
        if (seenConstructiveStages.has(card.stageIndex)) {
          problems.push(`申论环节 ${card.stageIndex} 存在多张策略卡`);
        }
        seenConstructiveStages.add(card.stageIndex);
        if (card.targetRole !== stage.speakerRole) {
          problems.push(`${label} targetRole 应为「${stage.speakerRole}」，实际「${card.targetRole}」`);
        }
        const allowed = stage.speakerSide === 'AFF' ? AFF_ARCHETYPES : NEG_ARCHETYPES;
        if (!allowed.has(card.archetype)) {
          problems.push(`${label} 的 archetype ${card.archetype} 与阵营 ${stage.speakerSide} 不匹配`);
        }
      }
      if (!isNonEmptyString(card.title)) problems.push(`${label} 缺 title`);
      if (!isNonEmptyString(card.coreMission)) problems.push(`${label} 缺 coreMission`);
      if (!card.modularScriptBlocks?.headerAnchor || !card.modularScriptBlocks?.bodyModule) {
        problems.push(`${label} 缺 modularScriptBlocks.headerAnchor/bodyModule`);
      }
      checkEvidenceRefs(card.mandatoryEvidenceIds, label);
      if ((card.mandatoryEvidenceIds ?? []).length === 0) warnings.push(`${label} 没有绑定必打论据`);
      if (!card.hiddenCounterTrap?.triggerCondition || !card.hiddenCounterTrap?.counterResponse) {
        problems.push(`${label} 缺 hiddenCounterTrap`);
      }
    }
    if (source.constructiveCards.length !== CONSTRUCTIVE_STAGES.length) {
      warnings.push(
        `申论策略卡应有 ${CONSTRUCTIVE_STAGES.length} 张，实际 ${source.constructiveCards.length} 张`,
      );
    }
  }

  /* 质询任务卡 */
  if (!Array.isArray(source.crossExCards)) {
    problems.push('crossExCards 必须是数组');
  } else {
    for (const card of source.crossExCards) {
      const label = `质询卡 ${card.cardId || '(无 id)'}`;
      if (!isNonEmptyString(card.cardId)) problems.push('质询卡缺 cardId');
      const stage = CROSS_EXAM_STAGES.find((s) => s.index === card.stageIndex);
      if (!stage) {
        problems.push(`${label} 的 stageIndex=${card.stageIndex} 不是质询环节`);
      } else if (card.targetRole !== stage.speakerRole) {
        problems.push(`${label} targetRole 应为「${stage.speakerRole}」，实际「${card.targetRole}」`);
      }
      if (card.mode !== 'TARGETED_COUNTER' && card.mode !== 'BLIND_RECON') {
        problems.push(`${label} mode 非法：${String(card.mode)}`);
      }
      if (card.mode === 'BLIND_RECON' && card.targetOpponentArchetype) {
        problems.push(`${label} 为 BLIND_RECON 时不得携带 targetOpponentArchetype`);
      }
      const chain = card.questionChain;
      if (!chain?.step1LockPremise || !chain?.step2ForceDilemma || !chain?.step3Crystallize) {
        problems.push(`${label} 三步题链不完整`);
      }
      if (!isNonEmptyString(card.tabooWarning)) problems.push(`${label} 缺 tabooWarning`);
    }
    if (source.crossExCards.length !== CROSS_EXAM_STAGES.length) {
      warnings.push(`质询任务卡应有 ${CROSS_EXAM_STAGES.length} 张，实际 ${source.crossExCards.length} 张`);
    }
  }

  /* 沙盘 */
  if (!Array.isArray(source.sandboxCards)) {
    problems.push('sandboxCards 必须是数组');
  } else {
    const topics = new Set<string>();
    for (const card of source.sandboxCards) {
      const label = `沙盘 ${card.cardId || '(无 id)'}`;
      if (!isNonEmptyString(card.cardId)) problems.push('沙盘缺 cardId');
      if (!isNonEmptyString(card.title)) problems.push(`${label} 缺 title`);
      if (!isNonEmptyString(card.crossfireQuestion)) problems.push(`${label} 缺 crossfireQuestion`);
      if (topics.has(card.topic)) problems.push(`沙盘主题重复：${card.topic}`);
      topics.add(card.topic);
      for (const side of ['AFF', 'NEG'] as const) {
        const perspective = card.perspectives?.[side];
        if (!perspective) {
          problems.push(`${label} 缺 ${side} 视角`);
          continue;
        }
        if (!Array.isArray(perspective.threeTurnOutline) || perspective.threeTurnOutline.length !== 3) {
          problems.push(`${label} ${side} 视角必须有 3 回合大纲`);
        }
        for (const turn of perspective.threeTurnOutline ?? []) {
          checkEvidenceRefs(turn.evidenceIds, `${label} ${side} 第 ${turn.turn} 回合`);
          if (!isNonEmptyString(turn.header) || !isNonEmptyString(turn.keyClaim)) {
            problems.push(`${label} ${side} 第 ${turn.turn} 回合缺 header/keyClaim`);
          }
        }
        checkEvidenceRefs(perspective.mustHitEvidenceIds, `${label} ${side} 必打数据`);
      }
    }
    if (source.sandboxCards.length !== SANDBOX_TOPIC_COUNT) {
      warnings.push(`沙盘应有 ${SANDBOX_TOPIC_COUNT} 张（三大核心决战沙盘池），实际 ${source.sandboxCards.length} 张`);
    }
  }

  /* DAG */
  const dagNodeIds = new Set<string>();
  if (!source.dag?.nodes || !source.dag?.edges) {
    problems.push('dag 必须包含 nodes 与 edges');
  } else {
    for (const node of source.dag.nodes) {
      if (!isNonEmptyString(node.id)) problems.push('DAG 节点缺 id');
      if (dagNodeIds.has(node.id)) problems.push(`DAG 节点 id 重复：${node.id}`);
      dagNodeIds.add(node.id);
      if (node.side !== 'AFF' && node.side !== 'NEG') problems.push(`DAG 节点 ${node.id} side 非法`);
      if (!Array.isArray(node.stageRange) || node.stageRange.length !== 2) {
        problems.push(`DAG 节点 ${node.id} 缺 stageRange`);
      }
      checkEvidenceRefs(node.evidenceIds, `DAG 节点 ${node.id}`);
    }
    for (const edge of source.dag.edges) {
      if (!dagNodeIds.has(edge.source)) problems.push(`DAG 边 ${edge.id} source 不存在：${edge.source}`);
      if (!dagNodeIds.has(edge.target)) problems.push(`DAG 边 ${edge.id} target 不存在：${edge.target}`);
    }
    const sides = new Set(source.dag.nodes.map((n) => n.side));
    if (!sides.has('AFF') || !sides.has('NEG')) warnings.push('DAG 应同时覆盖正反双方');
  }

  /* 评分表 */
  if (!source.coachRubric?.dimensions?.length) {
    problems.push('coachRubric 至少需要一个评分维度');
  } else {
    const totalWeight = source.coachRubric.dimensions.reduce((sum, d) => sum + d.weight, 0);
    if (Math.abs(totalWeight - 1) > 0.001) {
      warnings.push(`coachRubric 权重合计应为 1，实际 ${totalWeight}`);
    }
  }

  /* 组合级：每张申论卡必须能被发牌计划覆盖 */
  for (const plan of getPairDealPlans()) {
    if (plan.sandbox) continue;
    const constructiveStage = plan.constructiveSide
      ? STAGE_SCHEDULE.find((s) => s.index === plan.stageIndices[0] && s.cardSlot === 'CONSTRUCTIVE')
      : null;
    if (!constructiveStage) continue;
    if (!source.constructiveCards?.some((c) => c.stageIndex === constructiveStage.index)) {
      problems.push(`配对 ${plan.pairId} 缺少环节 ${constructiveStage.index} 的申论策略卡`);
    }
    const crossStage = STAGE_SCHEDULE.find(
      (s) => s.index === plan.stageIndices[1] && s.cardSlot === 'CROSS_EXAM',
    );
    if (crossStage && !source.crossExCards?.some((c) => c.stageIndex === crossStage.index)) {
      problems.push(`配对 ${plan.pairId} 缺少环节 ${crossStage.index} 的质询任务卡`);
    }
  }

  /* 规模建议（内容验收时要求为 0 warning） */
  if ((source.evidenceLibrary?.length ?? 0) < 30) {
    warnings.push(`权威论据库应 ≥ 30 条，实际 ${source.evidenceLibrary?.length ?? 0} 条`);
  }
  if (source.dag?.nodes && source.dag.nodes.length < 18) {
    warnings.push(`DAG 攻防树节点应 ≥ 18 个，实际 ${source.dag.nodes.length} 个`);
  }

  return { problems, warnings };
}

/* ------------------------------------------------------------------ */
/* 加载                                                                */
/* ------------------------------------------------------------------ */

/**
 * 解析题库源数据为运行期 `MotionPackage`：
 * - 校验结构完整性（有问题抛 MotionDataError）；
 * - 将申论卡的 evidence id 引用解析为完整 `mandatoryEvidences`。
 */
export function resolveMotionPackage(source: MotionPackageSource): MotionPackage {
  const { problems } = auditMotionPackage(source);
  if (problems.length > 0) throw new MotionDataError(problems);

  const evidenceById = new Map(source.evidenceLibrary.map((e) => [e.id, e]));
  const constructiveCards: ConstructiveStrategyCard[] = source.constructiveCards.map((card) => {
    const { mandatoryEvidenceIds, ...rest } = card;
    const mandatoryEvidences = mandatoryEvidenceIds
      .map((id) => evidenceById.get(id))
      .filter((e): e is MandatoryEvidence => Boolean(e));
    return { ...rest, mandatoryEvidences };
  });

  return {
    motionId: source.motionId,
    motion: source.motion,
    title: source.title,
    version: source.version,
    sides: source.sides,
    stages: [...STAGE_SCHEDULE],
    constructiveCards,
    crossExCards: source.crossExCards,
    sandboxCards: source.sandboxCards,
    evidenceLibrary: source.evidenceLibrary,
    dag: source.dag,
    coachRubric: source.coachRubric,
    coachingNotes: source.coachingNotes,
  };
}

/** 查询某环节应发出的申论卡 / 质询卡（沙盘单独处理） */
export function findConstructiveCard(
  motion: MotionPackage,
  stageIndex: number,
): ConstructiveStrategyCard | undefined {
  return motion.constructiveCards.find((c) => c.stageIndex === stageIndex);
}

export function findCrossExCard(
  motion: MotionPackage,
  stageIndex: number,
): CrossExTaskCard | undefined {
  return motion.crossExCards.find((c) => c.stageIndex === stageIndex);
}

/** 从沙盘库中随机抽取一张指定主题的沙盘（缺省全库随机；教师可指定） */
export function pickSandboxCard(
  motion: MotionPackage,
  options: { topic?: SandboxCard['topic']; random?: () => number; excludeCardIds?: string[] } = {},
): SandboxCard {
  const random = options.random ?? Math.random;
  let pool = motion.sandboxCards.filter((c) => !options.excludeCardIds?.includes(c.cardId));
  if (options.topic) pool = pool.filter((c) => c.topic === options.topic);
  if (pool.length === 0) {
    throw new Error('没有可用的沙盘卡片（请检查题库或排除列表）');
  }
  const index = Math.min(pool.length - 1, Math.floor(random() * pool.length));
  return pool[index] as SandboxCard;
}
